import React, { useState, useEffect, useRef, useCallback } from 'react'
import { X, User, Phone, Mail, MapPin, Home, Edit2, Trash2, Clock, AlertTriangle, CheckCircle, Search } from 'lucide-react'
import { formatPhoneInput, isValidPhone } from '../../utils/validation'

// ─── Tenant / Locale Defaults ─────────────────────────────────────────────────
const DEFAULT_COUNTRY = process.env.REACT_APP_DEFAULT_COUNTRY || 'India'

// ─── Smart Address Section ────────────────────────────────────────────────────
const AddressSection = ({ formData, onChange, errors, loading }) => {
  const country = formData.country || 'India'
  const countryConfig = COUNTRIES.find(c => c.value === country) || COUNTRIES[COUNTRIES.length - 1]
  const isUS = country === 'United States'
  const isIndia = country === 'India'

  // US address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [addressLoading, setAddressLoading] = useState(false)
  const suggestTimer = useRef(null)
  const suggestRef = useRef(null)

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) setShowSuggestions(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // US: Nominatim address autocomplete
  const fetchAddressSuggestions = useCallback(async (query) => {
    if (!query || query.length < 5) { setAddressSuggestions([]); return }
    setAddressLoading(true)
    try {
      // Validate/standardize address via USPS proxy on blur — for live typing
      // we use Census Geocoder which is CORS-friendly and uses USPS-sourced data
      const encoded = encodeURIComponent(query)
      const res = await fetch(
        `https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?address=${encoded}&benchmark=2020&format=json`
      )
      const data = await res.json()
      const matches = data?.result?.addressMatches || []
      setAddressSuggestions(matches.map(m => ({
        label: m.matchedAddress,
        address: [m.addressComponents.fromAddress, m.addressComponents.streetName,
        m.addressComponents.suffixType].filter(Boolean).join(' '),
        city: m.addressComponents.city || '',
        state: m.addressComponents.state || '',
        zip: m.addressComponents.zip || ''
      })))
      setShowSuggestions(matches.length > 0)
    } catch (e) {
      console.error('Address lookup failed', e)
    } finally {
      setAddressLoading(false)
    }
  }, [])

  const handleAddressInput = (e) => {
    onChange(e)
    if (isUS) {
      clearTimeout(suggestTimer.current)
      suggestTimer.current = setTimeout(() => fetchAddressSuggestions(e.target.value), 500)
    }
  }

  const pickSuggestion = (s) => {
    onChange({ target: { name: 'address', value: s.address || formData.address } })
    if (s.city) onChange({ target: { name: 'city', value: s.city } })
    if (s.state) onChange({ target: { name: 'state', value: s.state } })
    if (s.zip) onChange({ target: { name: 'pincode', value: s.zip } })
    setShowSuggestions(false)
    setAddressSuggestions([])
  }

  // India: pincode → city/state lookup
  const handlePincodeBlur = async () => {
    if (!isIndia || !formData.pincode || formData.pincode.length !== 6) return
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${formData.pincode}`)
      const data = await res.json()
      if (data[0]?.Status === 'Success') {
        const post = data[0].PostOffice[0]
        if (post.District && !formData.city) onChange({ target: { name: 'city', value: post.District } })
        if (post.State && !formData.state) onChange({ target: { name: 'state', value: post.State } })
      }
    } catch (e) { /* silent */ }
  }

  // US: ZIP → city/state via USPS proxy (backend: GET /api/utils/zip/:zip)
  const handleZipBlur = async () => {
    if (!isUS || !formData.pincode || formData.pincode.replace(/\D/g, '').length < 5) return
    try {
      const zip = formData.pincode.replace(/\D/g, '').slice(0, 5)
      const res = await fetch(`/api/utils/zip/${zip}`)
      if (!res.ok) return
      const data = await res.json()
      if (data.success) {
        if (data.city && !formData.city) onChange({ target: { name: 'city', value: data.city } })
        if (data.state && !formData.state) onChange({ target: { name: 'state', value: data.state } })
      }
    } catch (e) { /* silent */ }
  }

  const stateOptions = isUS ? US_STATES : isIndia ? INDIA_STATES : null

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900 border-b pb-2">Address Information (Optional)</h3>

      {/* Country selector — always first */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
        <select name="country" value={formData.country} onChange={onChange} disabled={loading}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white">
          {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {/* Street address with US autocomplete */}
      <div ref={isUS ? suggestRef : null} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Street Address
          {isUS && <span className="ml-2 text-xs text-blue-500 font-normal">✨ type to search</span>}
        </label>
        <div className="relative">
          <Home className="absolute left-3 top-3 text-gray-400" size={18} />
          {addressLoading && <Search className="absolute right-3 top-3 text-blue-400 animate-pulse" size={16} />}
          <textarea name="address" value={formData.address} onChange={handleAddressInput}
            onFocus={() => isUS && addressSuggestions.length > 0 && setShowSuggestions(true)}
            rows="2" disabled={loading}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={isUS ? 'Start typing address…' : isIndia ? 'House/Flat No., Building, Street' : 'Street address'} />
        </div>
        {/* Autocomplete dropdown */}
        {isUS && showSuggestions && addressSuggestions.length > 0 && (
          <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {addressSuggestions.map((s, i) => (
              <li key={i} onMouseDown={() => pickSuggestion(s)}
                className="px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b last:border-0 truncate">
                📍 {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* City + State */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isUS ? 'City' : isIndia ? 'City / District' : 'City'}
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" name="city" value={formData.city} onChange={onChange} disabled={loading}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={isUS ? 'New York' : isIndia ? 'Mumbai' : 'City'} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isUS ? 'State' : isIndia ? 'State / UT' : 'State / Province'}
          </label>
          {stateOptions ? (
            <select name="state" value={formData.state} onChange={onChange} disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">Select state…</option>
              {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input type="text" name="state" value={formData.state} onChange={onChange} disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="State / Province" />
          )}
        </div>
      </div>

      {/* ZIP / Pincode */}
      {countryConfig.zipLen > 0 || country === 'Other' ? (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {countryConfig.zip}
              {(isIndia || isUS) && <span className="ml-1 text-xs text-gray-400">(auto-fills city/state)</span>}
            </label>
            <input type="text" name="pincode" value={formData.pincode} onChange={onChange}
              onBlur={isIndia ? handlePincodeBlur : isUS ? handleZipBlur : undefined}
              disabled={loading}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.pincode ? 'border-red-500' : 'border-gray-300'}`}
              placeholder={isUS ? '10001' : isIndia ? '400001' : 'Postal code'}
              maxLength={countryConfig.zipLen || 10} />
            {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ─── Conflict Resolution Dialog ───────────────────────────────────────────────

const EMPTY_FORM = {
  first_name: '', last_name: '', name: '',
  phone: '', email: '', address: '',
  city: '', state: '', pincode: '', country: DEFAULT_COUNTRY
}

// ─── Country / Address Config ─────────────────────────────────────────────────
const COUNTRIES = [
  { value: 'India', label: '🇮🇳 India', zip: 'Pincode', zipLen: 6, zipPattern: /^\d{6}$/ },
  { value: 'United States', label: '🇺🇸 United States', zip: 'ZIP Code', zipLen: 5, zipPattern: /^\d{5}(-\d{4})?$/ },
  { value: 'United Kingdom', label: '🇬🇧 United Kingdom', zip: 'Postcode', zipLen: 8, zipPattern: null },
  { value: 'Canada', label: '🇨🇦 Canada', zip: 'Postal Code', zipLen: 7, zipPattern: null },
  { value: 'Australia', label: '🇦🇺 Australia', zip: 'Postcode', zipLen: 4, zipPattern: /^\d{4}$/ },
  { value: 'UAE', label: '🇦🇪 UAE', zip: 'Postal Code', zipLen: 0, zipPattern: null },
  { value: 'Singapore', label: '🇸🇬 Singapore', zip: 'Postal Code', zipLen: 6, zipPattern: /^\d{6}$/ },
  { value: 'Other', label: '🌍 Other', zip: 'Postal Code', zipLen: 0, zipPattern: null },
]

const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
]

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
]

// ─── Conflict Resolution Dialog ──────────────────────────────────────────────
const ConflictDialog = ({ existing, conflicts, nameMatch, matchedBy, onResolve, onUseExisting, onCancel }) => {
  const [resolutions, setResolutions] = useState(() => {
    const init = {}
    conflicts.forEach(c => { init[c.field] = 'keep_existing' })
    return init
  })

  const fieldLabel = (f) => ({
    name: 'Full Name', first_name: 'First Name', last_name: 'Last Name',
    email: 'Email', phone: 'Phone',
    address: 'Address', city: 'City', state: 'State',
    postal_code: 'Pincode', country: 'Country'
  }[f] || f)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <AlertTriangle className="text-amber-500" size={24} />
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Customer Already Exists</h3>
            <p className="text-sm text-gray-500">
              Found by <span className="font-medium">{matchedBy}</span>
              {!nameMatch && <span className="text-amber-600 ml-1">— name doesn't match</span>}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-80 overflow-y-auto">
          <div className="bg-blue-50 rounded-lg p-3 text-sm">
            <p className="font-semibold text-blue-800">Existing: {existing.name}</p>
            <p className="text-blue-600">{existing.phone}{existing.email ? ` · ${existing.email}` : ''}</p>
          </div>

          {conflicts.length === 0 ? (
            <p className="text-gray-600 text-sm">No field conflicts — this is the same customer.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 font-medium">Choose which value to keep for each conflicting field:</p>
              {conflicts.map(({ field, existingValue, newValue }) => (
                <div key={field} className="border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-semibold text-gray-700">{fieldLabel(field)}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-start gap-2 p-2 rounded cursor-pointer border-2 transition-colors ${resolutions[field] === 'keep_existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                      <input type="radio" className="mt-0.5" checked={resolutions[field] === 'keep_existing'}
                        onChange={() => setResolutions(r => ({ ...r, [field]: 'keep_existing' }))} />
                      <div>
                        <p className="text-xs text-gray-500">Keep existing</p>
                        <p className="text-sm font-medium text-gray-800">{existingValue}</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 p-2 rounded cursor-pointer border-2 transition-colors ${resolutions[field] === 'use_new' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                      <input type="radio" className="mt-0.5" checked={resolutions[field] === 'use_new'}
                        onChange={() => setResolutions(r => ({ ...r, [field]: 'use_new' }))} />
                      <div>
                        <p className="text-xs text-gray-500">Use new</p>
                        <p className="text-sm font-medium text-gray-800">{newValue}</p>
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex flex-col sm:flex-row gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
          <button onClick={onUseExisting} className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 text-sm">Use Existing (no changes)</button>
          {conflicts.length > 0 && (
            <button onClick={() => onResolve({ customerId: existing.id, fieldResolutions: resolutions })}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
              Apply Resolution
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Change History Tab ───────────────────────────────────────────────────────
const ChangeHistoryTab = ({ customerId }) => {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/customers/${customerId}/change-history`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => { if (res.success) setHistory(res.data) })
      .finally(() => setLoading(false))
  }, [customerId])

  const typeColor = (t) => ({
    created: 'text-green-600 bg-green-50',
    updated: 'text-blue-600 bg-blue-50',
    field_conflict_resolved: 'text-amber-600 bg-amber-50',
    deleted: 'text-red-600 bg-red-50'
  }[t] || 'text-gray-600 bg-gray-50')

  if (loading) return <div className="text-center py-8 text-gray-400">Loading history…</div>
  if (!history.length) return <div className="text-center py-8 text-gray-400">No changes recorded yet.</div>

  return (
    <div className="space-y-2">
      {history.map(h => (
        <div key={h.id} className="flex items-start gap-3 p-3 border rounded-lg">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${typeColor(h.change_type)}`}>
            {h.change_type.replace(/_/g, ' ')}
          </span>
          <div className="flex-1 min-w-0">
            {h.field_name && (
              <p className="text-sm text-gray-700">
                <span className="font-medium capitalize">{h.field_name.replace(/_/g, ' ')}</span>
                {h.old_value && <span className="text-gray-500"> "{h.old_value}"</span>}
                {h.old_value && h.new_value && <span className="text-gray-400"> → </span>}
                {h.new_value && <span className="text-gray-800"> "{h.new_value}"</span>}
              </p>
            )}
            {h.note && <p className="text-xs text-gray-400">{h.note}</p>}
            <p className="text-xs text-gray-400 mt-0.5">
              {h.changed_by_name || 'System'} · {new Date(h.changed_at).toLocaleString()}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
const CustomerFormModal = ({ isOpen, onClose, onCustomerCreated, customer = null, onCustomerDeleted }) => {
  const isEditMode = !!customer

  const [formData, setFormData] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [activeTab, setActiveTab] = useState('form')
  const [conflict, setConflict] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [schemeBlock, setSchemeBlock] = useState(null)  // { enrollments, message }

  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        pincode: customer.postal_code || '',
        country: customer.country || 'India'
      })
    } else {
      setFormData(EMPTY_FORM)
    }
    setActiveTab('form')
    setErrors({})
    setConflict(null)
    setDeleteConfirm(false)
  }, [customer, isOpen])

  const validateForm = () => {
    const newErrors = {}
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required'
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number (with an optional country code)'
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    const selectedCountry = COUNTRIES.find(c => c.value === (formData.country || 'India')) || COUNTRIES[COUNTRIES.length - 1]
    if (formData.pincode && selectedCountry.zipPattern && !selectedCountry.zipPattern.test(formData.pincode)) {
      newErrors.pincode = `Please enter a valid ${selectedCountry.zip}`
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const buildFullName = (first, last) => [first, last].filter(Boolean).join(' ').trim()

  const handleChange = (e) => {
    const { name } = e.target
    const value = name === 'phone' ? formatPhoneInput(e.target.value) : e.target.value
    setFormData(prev => {
      const updated = { ...prev, [name]: value }
      // Auto-merge first+last into full name
      if (name === 'first_name' || name === 'last_name') {
        updated.name = buildFullName(
          name === 'first_name' ? value : prev.first_name,
          name === 'last_name' ? value : prev.last_name
        )
      }
      return updated
    })
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleEditSubmit = async () => {
    if (!validateForm()) return
    setLoading(true)
    try {
      const res = await fetch(`/api/customers/${customer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, postal_code: formData.pincode, phone: formData.phone.replace(/\s/g, '') })
      })
      const result = await res.json()
      if (!result.success) throw new Error(result.error || 'Update failed')
      onCustomerCreated(result.data)
      handleClose()
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSubmit = async () => {
    if (!validateForm()) return
    setLoading(true)
    try {
      const payload = { ...formData, postal_code: formData.pincode, phone: formData.phone.replace(/\s/g, '') }
      const checkRes = await fetch('/api/customers/check-duplicate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      })
      const checkResult = await checkRes.json()
      if (checkResult.data?.found) {
        setConflict({ ...checkResult.data, incomingData: payload })
        return
      }
      await submitToApi(payload, null)
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const submitToApi = async (payload, resolution) => {
    const body = resolution ? { ...payload, resolution } : payload
    const res = await fetch('/api/customers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    })
    const result = await res.json()
    if (!result.success) throw new Error(result.error || 'Failed')
    onCustomerCreated(result.data)
    handleClose()
  }

  const handleUseExisting = () => {
    onCustomerCreated(conflict.customer)
    handleClose()
  }

  const handleResolveConflict = async (resolution) => {
    setConflict(null)
    setLoading(true)
    try {
      await submitToApi({ ...formData, phone: formData.phone.replace(/\s/g, '') }, resolution)
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/customers/${customer.id}`, { method: 'DELETE', credentials: 'include' })
      const result = await res.json()
      if (!result.success) {
        if (result.code === 'SCHEME_ENROLLMENT_ACTIVE') {
          setDeleteConfirm(false)
          setSchemeBlock({ enrollments: result.enrollments, message: result.error })
          return
        }
        throw new Error(result.error || 'Delete failed')
      }
      if (onCustomerDeleted) onCustomerDeleted(customer.id)
      handleClose()
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setLoading(false)
      setDeleteConfirm(false)
    }
  }

  const handleClose = () => {
    setFormData(EMPTY_FORM)
    setErrors({})
    setConflict(null)
    setDeleteConfirm(false)
    setSchemeBlock(null)
    setActiveTab('form')
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {conflict && (
        <ConflictDialog
          existing={conflict.customer}
          conflicts={conflict.conflicts}
          nameMatch={conflict.nameMatch}
          matchedBy={conflict.matchedBy}
          onResolve={handleResolveConflict}
          onUseExisting={handleUseExisting}
          onCancel={() => setConflict(null)}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <Trash2 className="text-red-500 mx-auto mb-3" size={36} />
            <h3 className="font-bold text-gray-900 text-lg mb-1">Delete Customer?</h3>
            <p className="text-gray-500 text-sm mb-4">
              This will remove <span className="font-medium">{customer?.name}</span>. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteConfirm(false)} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={loading} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {loading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {schemeBlock && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0" size={28} />
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Cannot Delete Customer</h3>
                <p className="text-sm text-gray-500">{customer?.name}</p>
              </div>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                This customer is enrolled in <span className="font-semibold text-amber-700">
                  {schemeBlock.enrollments?.length} active Gold Scheme{schemeBlock.enrollments?.length > 1 ? 's' : ''}
                </span>. Complete or cancel all enrollments before deleting.
              </p>
              <div className="space-y-2">
                {schemeBlock.enrollments?.map(e => (
                  <div key={e.id} className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-amber-800">{e.scheme_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {e.status}
                      </span>
                    </div>
                    <div className="text-gray-600 mt-1 flex gap-4">
                      <span>Paid: ₹{parseFloat(e.paid_amount || 0).toLocaleString('en-IN')}</span>
                      <span>Matures: {new Date(e.maturity_date).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => setSchemeBlock(null)}
                className="px-5 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {isEditMode
                ? <><Edit2 className="text-amber-500" size={22} /> Edit — {customer.name}</>
                : <><User className="text-blue-600" size={22} /> Create New Customer</>
              }
            </h2>
            <div className="flex items-center gap-2">
              {isEditMode && onCustomerDeleted && (
                <button onClick={() => setDeleteConfirm(true)} className="text-red-400 hover:text-red-600 p-1" title="Delete" disabled={loading}>
                  <Trash2 size={20} />
                </button>
              )}
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" disabled={loading}><X size={24} /></button>
            </div>
          </div>

          {/* Tabs */}
          {isEditMode && (
            <div className="flex border-b px-6">
              {[{ id: 'form', label: 'Details' }, { id: 'history', label: 'Change History', icon: <Clock size={14} /> }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === tab.id ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="p-6">
            {activeTab === 'history' && isEditMode && <ChangeHistoryTab customerId={customer.id} />}

            {activeTab === 'form' && (
              <form onSubmit={e => { e.preventDefault(); isEditMode ? handleEditSubmit() : handleCreateSubmit() }} className="space-y-6">

                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 border-b pb-2">Basic Information</h3>

                  {/* First Name + Last Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} disabled={loading}
                          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="First name" />
                      </div>
                      {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} disabled={loading}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Last name (optional)" />
                    </div>
                  </div>

                  {/* Full Name — auto-filled, editable override */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                      <span className="ml-2 text-xs text-gray-400 font-normal">(auto-filled from First + Last)</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="text" name="name" value={formData.name} onChange={handleChange} disabled={loading}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-700"
                        placeholder="Full name" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={loading}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="10-digit mobile number" maxLength="10" />
                    </div>
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address (Optional)</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={loading}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="customer@example.com" />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <AddressSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  loading={loading}
                />

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={handleClose} disabled={loading}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}
                    className={`px-6 py-2 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 ${isEditMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {loading
                      ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />{isEditMode ? 'Saving…' : 'Checking…'}</>
                      : <><CheckCircle size={16} />{isEditMode ? 'Save Changes' : 'Create Customer'}</>
                    }
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CustomerFormModal
